import { createContext, useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { ChatFileModal } from 'features/chat/components/ChatAttachment/ChatFileModal';
import { ChatFilesPreview } from 'features/chat/components/ChatAttachment/ChatFilesPreview';
import { ChatMediaFilesPreview } from 'features/chat/components/ChatAttachment/ChatMediaFilesPreview';
import { ChatDragAndDropPopover } from 'features/chat/components/ChatDragAndDropPopover';
import { ChatMessageScheduledAttachmentModal } from 'features/chat/components/ChatMessagesScheduling/ScheduledAttachmentModal';
import { ATTACHMENT_TYPES } from 'features/chat/config';
import { UnuploadedAttachment } from 'features/chat/config/types/Attachment';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';

import { SIZES, modal } from 'shared/components/Modal';

import { ChatFileSchedulingModal } from '../components/ChatAttachment/ChatFileSchedulingModal';
import { useChatFormAttachmentSubmit } from '../hooks/useChatFormAttachmentSubmit';
import { useChatMessageSchedulingContext } from './chatMessageScheduling';

const ChatAttachmentsContext = createContext({
  handleFileChange: (files: File[]) => {},
  handleMediaChange: (files: File[]) => {},
});

type ChatAttachmentsProviderPropTypes = {
  children: React.ReactNode;
};

export const ChatAttachmentsProvider = ({
  children,
}: ChatAttachmentsProviderPropTypes) => {
  const methods = useFormContext();
  const { sendMessageWithAttachments } = useChatMessagesContext();

  const { onFormSubmitWithAttachment } = useChatFormAttachmentSubmit();

  const { isScheduledListVisible: isSchedulingMode } =
    useChatMessageSchedulingContext();

  const showScheduleAttachmentModal = useCallback(
    (changeHandler: (files: File[]) => void) =>
      (text: string, files: UnuploadedAttachment[]) => {
        modal.open({
          content: (
            <ChatMessageScheduledAttachmentModal
              onBackButtonClick={changeHandler}
              onSubmit={onFormSubmitWithAttachment}
            />
          ),
          props: { size: SIZES.AUTO },
          context: {
            text,
            files,
          },
        });
      },
    [onFormSubmitWithAttachment]
  );

  const FileModal = isSchedulingMode ? ChatFileSchedulingModal : ChatFileModal;

  const handleFileChange = useCallback(
    (files: File[]) => {
      if (!files) {
        return;
      }

      modal.open({
        content: (
          <FileModal
            files={files}
            scheduleSubmitHandler={showScheduleAttachmentModal(
              handleFileChange
            )}
            onSubmit={
              isSchedulingMode
                ? showScheduleAttachmentModal(handleFileChange)
                : onFormSubmitWithAttachment
            }
            type={ATTACHMENT_TYPES.FILE}
          >
            <ChatFilesPreview />
          </FileModal>
        ),
        props: {
          title: '',
          size: SIZES.SM,
        },
        context: {
          chatFormMethods: methods,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSchedulingMode]
  );

  const handleMediaChange = useCallback(
    (files: File[]) => {
      if (!files) {
        return;
      }

      modal.open({
        content: (
          <FileModal
            files={files}
            scheduleSubmitHandler={showScheduleAttachmentModal(
              handleMediaChange
            )}
            type={ATTACHMENT_TYPES.MEDIA}
            onSubmit={
              isSchedulingMode
                ? showScheduleAttachmentModal(handleMediaChange)
                : onFormSubmitWithAttachment
            }
          >
            <ChatMediaFilesPreview />
          </FileModal>
        ),
        props: {
          title: '',
          size: SIZES.SM,
        },
        context: {
          chatFormMethods: methods,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      methods,
      sendMessageWithAttachments,
      isSchedulingMode,
      onFormSubmitWithAttachment,
      showScheduleAttachmentModal,
    ]
  );

  const providerValue = useMemo(
    () => ({
      handleFileChange,
      handleMediaChange,
    }),
    [handleFileChange, handleMediaChange]
  );

  return (
    <ChatAttachmentsContext.Provider value={providerValue}>
      <ChatDragAndDropPopover
        onDrop={(file) => file && handleFileChange([file])}
      >
        {children}
      </ChatDragAndDropPopover>
    </ChatAttachmentsContext.Provider>
  );
};

export const useChatAttachmentsContext = () => {
  const chatAttachmentsContext = useContext(ChatAttachmentsContext);

  if (!chatAttachmentsContext) {
    throw new Error(
      'useChatAttachmentsContext must be used in ChatAttachmentsContext.Provider'
    );
  }

  return chatAttachmentsContext;
};
